import { useState, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    Alert,
    LinearProgress,
    Paper,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { ModernHeader } from "../../../components/modern";

interface UploadResult {
    message: string;
    fileName: string;
    chunkCount: number;
}

export const KnowledgeBase = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<UploadResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        if (selected.type !== "application/pdf") {
            setError("Only PDF files are accepted.");
            setFile(null);
            return;
        }

        if (selected.size > 20 * 1024 * 1024) {
            setError("File size must be under 20 MB.");
            setFile(null);
            return;
        }

        setFile(selected);
        setError(null);
        setResult(null);
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setError(null);
        setResult(null);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/knowledge/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setResult(response.data);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                "Upload failed. Please try again.";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setUploading(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setError(null);
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", pb: 4 }}>
            <ModernHeader
                title="Knowledge Base"
                subtitle="Upload clinical PDFs to the AI therapist's knowledge base"
            />

            <Box px={3} maxWidth={720}>
                {/* Upload Area */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        borderRadius: "16px",
                        border: "2px dashed",
                        borderColor: file ? "#D58E4E" : "rgba(0,0,0,0.12)",
                        backgroundColor: file ? "rgba(213,142,78,0.04)" : "#fff",
                        textAlign: "center",
                        cursor: uploading ? "default" : "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": uploading
                            ? {}
                            : {
                                borderColor: "#D58E4E",
                                backgroundColor: "rgba(213,142,78,0.04)",
                            },
                    }}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        hidden
                        onChange={handleFileSelect}
                    />

                    {!file ? (
                        <>
                            <CloudUploadIcon
                                sx={{ fontSize: 48, color: "#bbb", mb: 1.5 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Drop a PDF here or click to browse
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Clinical documents only — max 20 MB
                            </Typography>
                        </>
                    ) : (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1.5}
                        >
                            <InsertDriveFileIcon sx={{ color: "#D58E4E", fontSize: 32 }} />
                            <Box textAlign="left">
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatSize(file.size)}
                                </Typography>
                            </Box>
                            {!uploading && (
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClear();
                                    }}
                                    sx={{ minWidth: "auto", ml: 1 }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </Button>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Progress */}
                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress
                            sx={{
                                borderRadius: 4,
                                height: 6,
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: "#D58E4E",
                                },
                                backgroundColor: "rgba(213,142,78,0.15)",
                            }}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, textAlign: "center" }}
                        >
                            Validating content, extracting exercises, and indexing — this
                            may take a moment…
                        </Typography>
                    </Box>
                )}

                {/* Upload Button */}
                <Button
                    variant="contained"
                    fullWidth
                    disabled={!file || uploading}
                    onClick={handleUpload}
                    startIcon={
                        uploading ? undefined : <CloudUploadIcon />
                    }
                    sx={{
                        mt: 3,
                        py: 1.5,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        backgroundColor: "#D58E4E",
                        "&:hover": { backgroundColor: "#c17a3e" },
                        "&.Mui-disabled": {
                            backgroundColor: "rgba(213,142,78,0.3)",
                            color: "#fff",
                        },
                    }}
                >
                    {uploading ? "Processing…" : "Upload & Process"}
                </Button>

                {/* Success */}
                {result && (
                    <Alert
                        icon={<CheckCircleIcon />}
                        severity="success"
                        sx={{ mt: 3, borderRadius: "12px" }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {result.message}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {result.chunkCount} knowledge chunks indexed into the AI
                            therapist's memory.
                        </Typography>
                    </Alert>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mt: 3, borderRadius: "12px" }}>
                        {error}
                    </Alert>
                )}
            </Box>
        </Box>
    );
};

export default KnowledgeBase;
